import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEVERITY_BADGE } from "./report_rendering/config";

export type BiteLink = { sessionId: string; sk: string; issueId: string };

type RawMessage = { role?: string; content?: unknown };

type BiteDetail = {
  lessonName: string | null;
  slide: {
    sessionId: string;
    sk: string;
    userId: string;
    lessonId: number;
    slideIndex: number;
    sessionStartTs: number;
    slideStartTs: number;
    slideEndTs: number;
    inactiveTime: number;
    offTabTime: number;
    messages: RawMessage[];
  };
  judgeResult: {
    id: number;
    quality_score: number | null;
    summary: string | null;
    positive_notes: string[] | null;
    username: string | null;
    name: string | null;
    session_start_ts: number | null;
    attempt_id: number | null;
  } | null;
  issues: {
    id: number;
    severity: string;
    category: string;
    title: string;
    evidence: string | null;
    explanation: string | null;
  }[];
  attempt: {
    slide_start_ts: number | null;
    attempt_end_ts: number | null;
    time_spent_ms: number | null;
    qa_total: number | null;
    qa_incorrect: number | null;
    attempt_status: string | null;
  } | null;
};

function extractText(content: unknown): string {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((b) => (b && typeof b === "object" && "text" in b ? String((b as { text: unknown }).text) : ""))
      .filter(Boolean)
      .join("\n");
  }
  return "";
}

function SystemPromptBlock({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide">System Prompt</p>
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {open ? "collapse" : "expand"}
        </button>
      </div>
      {open && (
        <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words max-h-72 overflow-y-auto leading-relaxed">
          {text}
        </pre>
      )}
    </div>
  );
}

export function BitePanel({ bite, width, onClose }: { bite: BiteLink; width: number; onClose: () => void }) {
  const [detail, setDetail] = useState<BiteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const highlightIssueId = Number(bite.issueId);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setDetail(null);

    const ctrl = new AbortController();
    const params = new URLSearchParams({ sessionId: bite.sessionId, sk: bite.sk });
    fetch(`/api/internal/admin/bites?${params}`, { signal: ctrl.signal })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json() as Promise<BiteDetail>; })
      .then(setDetail)
      .catch((e) => { if ((e as { name?: string }).name !== "AbortError") setError(true); })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [bite.sessionId, bite.sk]);

  const slide = detail?.slide;
  const judgeResult = detail?.judgeResult;
  const rawIssues = detail?.issues ?? [];
  const issues = highlightIssueId
    ? [...rawIssues].sort((a, b) => (b.id === highlightIssueId ? 1 : 0) - (a.id === highlightIssueId ? 1 : 0))
    : rawIssues;

  const systemMsg = slide?.messages.find((m) => m.role?.toLowerCase() === "system");
  const convo = slide?.messages.filter((m) => m.role?.toLowerCase() !== "system") ?? [];

  return (
    <div className="rounded-xl border bg-card shadow-sm flex flex-col shrink-0" style={{ width }}>
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Bite Detail</p>
        <Button variant="ghost" size="icon" className="size-6" onClick={onClose}>
          <X className="size-3" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        )}
        {error && <p className="text-sm text-destructive">Failed to load bite.</p>}

        {slide && (
          <>
            {/* Metadata */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold">
                  {detail.lessonName ? `${detail.lessonName} (${slide.lessonId})` : `Lesson ${slide.lessonId}`} — Slide {slide.slideIndex}
                </p>
                {judgeResult?.quality_score != null && (
                  <span className="text-sm font-semibold text-red-400">
                    {judgeResult.quality_score}/5
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                {judgeResult?.name && (
                  <span>Name: <span className="font-medium text-foreground">{judgeResult.name}</span></span>
                )}
                {detail?.attempt && (
                  <div className="flex flex-wrap gap-x-3">
                    {detail.attempt.time_spent_ms != null && (
                      <span>Attempt active: <span className="text-foreground">{(detail.attempt.time_spent_ms / 1000).toFixed(0)}s</span></span>
                    )}
                    {slide.slideEndTs > 0 && (
                      <span>Bite end: <span className="text-foreground">{new Date(slide.slideEndTs).toISOString().replace("T", " ").slice(0, 19)} UTC</span></span>
                    )}
                    {detail.attempt.attempt_status === "completed" && detail.attempt.qa_total != null && (
                      <span>Q&amp;A: <span className="text-foreground">{detail.attempt.qa_incorrect ?? 0} incorrect / {detail.attempt.qa_total} total</span></span>
                    )}
                    {detail.attempt.attempt_status != null && detail.attempt.attempt_status !== "completed" && (
                      <span>Status: <span className="text-foreground">incomplete</span></span>
                    )}
                  </div>
                )}
                {slide.userId && slide.sessionStartTs > 0 && slide.slideEndTs > 0 && (
                  <a
                    href={`https://us3.datadoghq.com/rum/sessions?query=%40type%3Asession%20%40usr.id%3A${slide.userId}&cols=&fromUser=true&from_ts=${new Date(new Date(slide.sessionStartTs).toISOString().slice(0, 10)).getTime()}&to_ts=${slide.slideEndTs + 5 * 60 * 1000}&live=false`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline w-fit"
                  >
                    Datadog RUM ↗
                  </a>
                )}

                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground select-none">Detail info</summary>
                  <div className="flex flex-col gap-0.5 mt-1 pl-2 border-l border-border">
                    <div className="flex flex-wrap gap-x-3">
                      {judgeResult?.username && (
                        <span>Username: <span className="text-foreground">{judgeResult.username}</span></span>
                      )}
                      <span>User ID: <span className="text-foreground">{slide.userId}</span></span>
                    </div>
                    <div className="flex flex-wrap gap-x-3">
                      <span>Session: <span className="font-mono text-foreground">{slide.sessionId}</span></span>
                      <span>SK: <span className="font-mono text-foreground">{slide.sk}</span></span>
                    </div>
                    {judgeResult?.attempt_id != null && (
                      <span>Attempt ID: <span className="font-mono text-foreground">{judgeResult.attempt_id}</span></span>
                    )}
                    <div className="flex flex-wrap gap-x-3">
                      {slide.sessionStartTs > 0 && (
                        <span>Session start: <span className="text-foreground">{new Date(slide.sessionStartTs).toISOString().replace("T", " ").slice(0, 19)} UTC</span></span>
                      )}
                      {slide.slideStartTs > 0 && (
                        <span>Bite start: <span className="text-foreground">{new Date(slide.slideStartTs).toISOString().replace("T", " ").slice(0, 19)} UTC</span></span>
                      )}
                      {slide.slideEndTs > 0 && (
                        <span>Bite end: <span className="text-foreground">{new Date(slide.slideEndTs).toISOString().replace("T", " ").slice(0, 19)} UTC</span></span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3">
                      {detail?.attempt?.slide_start_ts != null && (
                        <span>Attempt start: <span className="text-foreground">{new Date(detail.attempt.slide_start_ts).toISOString().replace("T", " ").slice(0, 19)} UTC</span></span>
                      )}
                      {detail?.attempt?.attempt_end_ts != null && (
                        <span>Attempt end: <span className="text-foreground">{new Date(detail.attempt.attempt_end_ts).toISOString().replace("T", " ").slice(0, 19)} UTC</span></span>
                      )}
                    </div>
                  </div>
                </details>
              </div>
            </div>

            {/* Judge Assessment */}
            {(judgeResult?.summary || issues.length > 0) && (
              <div className="rounded-lg border border-border bg-card px-4 py-3 flex flex-col gap-3">
                <p className="text-xs font-bold uppercase tracking-wide">Judge Assessment</p>

                {judgeResult?.summary && (
                  <details className="group">
                    <summary className="text-xs text-muted-foreground cursor-pointer select-none list-none flex items-center gap-1.5 hover:text-foreground transition-colors">
                      <span className="text-[10px] opacity-50 group-open:rotate-90 transition-transform inline-block">▶</span> Summary
                    </summary>
                    <p className="text-sm text-foreground leading-relaxed mt-2">{judgeResult.summary}</p>
                  </details>
                )}

                  {issues.length > 0 && (
                    <div className="rounded-lg border border-border overflow-x-auto">
                      <table className="table-auto min-w-[700px] w-full text-xs">
                        <thead>
                          <tr className="border-b border-border bg-muted/50">
                            <th className="text-left px-3 py-2 font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">Severity</th>
                            <th className="text-left px-3 py-2 font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">Category</th>
                            <th className="text-left px-3 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Title</th>
                            <th className="text-left px-3 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Evidence</th>
                            <th className="text-left px-3 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Explanation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {issues.map((iss) => {
                            const isHighlighted = iss.id === highlightIssueId;
                            const isDimmed = highlightIssueId && !isHighlighted;
                            return (
                            <tr
                              key={iss.id}
                              className={`border-b border-border last:border-0 ${
                                isHighlighted
                                  ? "border-l-2 border-l-primary bg-primary/10"
                                  : isDimmed ? "opacity-40" : ""
                              }`}
                            >
                              <td className="px-3 py-2 align-top whitespace-nowrap">
                                <div className="flex flex-col gap-1">
                                  <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${SEVERITY_BADGE[iss.severity] ?? "bg-muted text-muted-foreground"}`}>
                                    {iss.severity}
                                  </span>
                                  {isHighlighted && (
                                    <span className="text-[9px] font-medium text-primary uppercase tracking-wide">↑ referenced</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 align-top text-muted-foreground whitespace-nowrap">{iss.category}</td>
                              <td className="px-3 py-2 align-top font-semibold text-foreground">{iss.title}</td>
                              <td className="px-3 py-2 align-top text-muted-foreground">{iss.evidence}</td>
                              <td className="px-3 py-2 align-top text-muted-foreground">{iss.explanation}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
            )}

            {/* System prompt */}
            {systemMsg && <SystemPromptBlock text={extractText(systemMsg.content)} />}

            {/* Conversation */}
            <div className="rounded-lg border border-border bg-card px-4 py-3 flex flex-col gap-2">
              <p className="text-xs font-bold uppercase tracking-wide">
                Conversation ({convo.length} messages)
              </p>
              {convo.length === 0 ? (
                <p className="text-xs text-muted-foreground">No messages</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {convo.map((msg, i) => {
                    const role = msg.role ?? "unknown";
                    const text = extractText(msg.content);
                    const isUser = role === "user";
                    const isAssistant = role === "assistant";
                    return (
                      <div key={i} className={`flex flex-col gap-1 ${isUser ? "items-end" : "items-start"}`}>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1">
                          {isUser ? "Student" : isAssistant ? "Tutor" : role}
                        </span>
                        <div
                          className={`rounded-lg px-3 py-2 text-xs max-w-[88%] whitespace-pre-wrap break-words ${
                            isUser
                              ? "bg-blue-900/40 border border-blue-800/50 text-foreground"
                              : isAssistant
                              ? "bg-muted border border-border text-foreground"
                              : "bg-muted/40 border border-border text-muted-foreground"
                          }`}
                        >
                          {text}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
